import { useState } from "react";
import axios from "axios";
import { Link2, Loader2, X, Check, Youtube, Play, Music, Video } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

/**
 * Reusable "Add Media by URL" picker. Detects YouTube / YouTube Shorts /
 * Vimeo / direct video / audio links, shows a live preview, and pushes a
 * structured media item to the parent.
 *
 * onAdd(item) — called with the saved object:
 *   { id, url, provider, video_id, embed_url, thumbnail, type, caption, title }
 *
 * acceptType — "video" (shorts) or "any" (gallery / hero)
 */
export default function MediaUrlAdder({ onAdd, acceptType = "any", buttonLabel = "Add by URL", className = "" }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setUrl("");
    setMeta(null);
    setError("");
    setLoading(false);
  };

  const lookup = async (rawUrl) => {
    setError("");
    setMeta(null);
    if (!rawUrl) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/media/embed-info`, { url: rawUrl });
      if (acceptType === "video" && res.data.type === "audio") {
        setError("This section only accepts video links.");
        return;
      }
      setMeta(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not read that URL.");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    if (!meta) return;
    const item = {
      id: `${meta.provider}-${meta.video_id || Date.now()}`,
      url: meta.url,
      provider: meta.provider,
      video_id: meta.video_id || null,
      embed_url: meta.embed_url || null,
      thumbnail: meta.thumbnail || null,
      title: meta.title || "",
      type: meta.type,
      caption: meta.title || "",
    };
    onAdd(item);
    toast.success("Added to listing — click Save to publish.");
    reset();
    setOpen(false);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/15 text-white text-sm font-medium transition ${className}`}
        data-testid="media-add-url-btn"
      >
        <Link2 className="w-4 h-4" /> {buttonLabel}
      </button>
    );
  }

  return (
    <div className={`rounded-xl border border-[#7C3AED]/40 bg-[#0F172A] p-4 space-y-3 ${className}`} data-testid="media-url-adder">
      <div className="flex items-center justify-between">
        <h4 className="text-white text-sm font-semibold flex items-center gap-2">
          <Link2 className="w-4 h-4 text-[#7C3AED]" /> Add Media by URL
        </h4>
        <button
          type="button"
          onClick={() => { reset(); setOpen(false); }}
          className="text-[#94A3B8] hover:text-white"
          aria-label="Close"
          data-testid="media-url-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <p className="text-xs text-[#94A3B8] leading-relaxed">
        Paste a YouTube, YouTube Shorts, Vimeo, .mp4 video, or .mp3 audio link.
      </p>

      <div className="flex gap-2">
        <Input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://youtu.be/… or https://vimeo.com/…"
          className="bg-[#020817] border-white/10 text-white text-sm"
          data-testid="media-url-input"
        />
        <Button
          type="button"
          onClick={() => lookup(url)}
          disabled={loading || !url}
          className="bg-[#7C3AED] hover:bg-[#8B5CF6] text-white"
          data-testid="media-url-preview-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Preview"}
        </Button>
      </div>

      {error && (
        <p className="text-xs text-red-400" data-testid="media-url-error">{error}</p>
      )}

      {meta && <PreviewBlock meta={meta} />}

      {meta && (
        <div className="flex justify-end gap-2 pt-1">
          <Button
            type="button"
            variant="outline"
            onClick={reset}
            className="border-white/20 bg-white/5 hover:bg-white/10 text-white text-sm"
            data-testid="media-url-clear-btn"
          >
            Clear
          </Button>
          <Button
            type="button"
            onClick={handleAdd}
            className="bg-gradient-to-r from-[#EC4899] to-[#7C3AED] hover:opacity-90 text-white text-sm"
            data-testid="media-url-add-btn"
          >
            <Check className="w-4 h-4 mr-1" /> Add to listing
          </Button>
        </div>
      )}
    </div>
  );
}

function PreviewBlock({ meta }) {
  const providerIcon = {
    youtube: <Youtube className="w-3.5 h-3.5 text-[#FF0000]" />,
    youtube_shorts: <Youtube className="w-3.5 h-3.5 text-[#FF0000]" />,
    vimeo: <Play className="w-3.5 h-3.5 text-[#1AB7EA]" />,
    direct_video: <Video className="w-3.5 h-3.5 text-[#7C3AED]" />,
    audio: <Music className="w-3.5 h-3.5 text-[#EC4899]" />,
  }[meta.provider];

  return (
    <div className="rounded-lg overflow-hidden border border-white/10 bg-black" data-testid="media-url-preview">
      <div className="flex items-center gap-2 px-3 py-2 bg-white/[0.04] border-b border-white/5">
        {providerIcon}
        <span className="text-xs text-white capitalize">{meta.provider.replace("_", " ")}</span>
        {meta.is_short && (
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#EC4899]/20 text-[#EC4899] font-semibold">Vertical</span>
        )}
        {meta.title && <span className="ml-2 text-xs text-[#94A3B8] truncate">— {meta.title}</span>}
      </div>
      <div className={meta.is_short ? "aspect-[9/16] max-h-[420px] mx-auto" : "aspect-video"}>
        {meta.embed_url ? (
          <iframe
            src={meta.embed_url}
            title={meta.title || "Preview"}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
            style={{ border: 0 }}
          />
        ) : meta.type === "audio" ? (
          <audio src={meta.url} controls className="w-full mt-4" />
        ) : (
          <video src={meta.url} controls className="w-full h-full bg-black" playsInline />
        )}
      </div>
    </div>
  );
}
